/**
 * Card utility functions
 *
 * Provides card masking and validation utilities.
 */

import creditCardType from 'credit-card-type';
import type { CardType } from '../types/voice-dtmf.types';

/**
 * Mask card number following Sycurio standard
 * Shows first 6 digits (BIN), masks middle, shows last 4 digits
 *
 * @param cardNumber - Card number (with or without spaces)
 * @returns Masked card number (4111 11** **** 1234)
 */
export const maskCardNumber = (cardNumber: string): string => {
  const clean = cardNumber.replace(/\s/g, '');
  const first6 = clean.slice(0, 6);
  const last4 = clean.slice(-4);

  // Format: "4111 11** **** 1234" (Sycurio standard)
  return `${first6.slice(0, 4)} ${first6.slice(4, 6)}** **** ${last4}`;
};

/**
 * Mask card number to show only last 4 digits
 * Used for display on the success screen (PCI-friendly)
 *
 * @param maskedCardNumber - Card number (full PAN or already partially masked)
 * @returns Card number with only last 4 digits visible (**** **** **** 1234)
 */
export const maskToLastFour = (maskedCardNumber: string): string => {
  if (!maskedCardNumber) return '';
  const digits = maskedCardNumber.replace(/\D/g, '');
  const lastFour = digits.slice(-4);
  if (lastFour.length < 4) return maskedCardNumber;
  return `**** **** **** ${lastFour}`;
};

/**
 * Detect card type from a masked card number using BIN prefix lookup.
 *
 * Extracts visible digits from a masked value (e.g. "4111 11** ****" → "411111")
 * and uses the credit-card-type library to identify the issuer.
 *
 * Returns a CardType only when there is exactly 1 unambiguous match — never
 * when multiple brands are still possible. Returns undefined (not UNKNOWN) when
 * uncertain, so the badge stays hidden rather than showing the wrong brand.
 *
 * @param maskedValue - Masked card number as received from Sycurio SSE (e.g. "4111 11** ****")
 * @returns CardType when unambiguously detected, undefined otherwise
 */
export const detectCardType = (maskedValue: string): CardType | undefined => {
  const visibleDigits = maskedValue.replace(/[^0-9]/g, '');
  if (!visibleDigits) return undefined;

  const matches = creditCardType(visibleDigits);
  if (matches.length !== 1) return undefined;

  switch (matches[0].type) {
    case 'visa':
      return 'VISA';
    case 'mastercard':
      return 'MASTERCARD';
    case 'american-express':
      return 'AMEX';
    default:
      return undefined;
  }
};

/**
 * Mask CVV following Sycurio standard
 *
 * @param cvv - CVV code
 * @returns Masked CVV (***)
 */
export const maskCVV = (cvv: string): string => {
  return '*'.repeat(cvv.length);
};

/**
 * Get CVV length for card type
 *
 * IMPORTANT: This function is MOCK/TEST-ONLY for QA simulation.
 *
 * In PRODUCTION, CVV length is managed by Sycurio (DTMF capture system).
 * We receive the masked CVV value from Sycurio via SSE events - we do NOT
 * control or validate CVV length. Sycurio handles this based on card type.
 *
 * Only use this in mock data generators for testing purposes.
 *
 * @param cardType - Card type
 * @returns CVV length (3 for most cards, 4 for Amex)
 */
export const getCVVLength = (cardType: CardType): number => {
  // MOCK-ONLY: Sycurio controls this in production
  return cardType === 'AMEX' ? 4 : 3;
};
